import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { MetadataTypesEnum } from '../enums';
import { timer, from } from 'rxjs';
import { concatMap, filter, take } from 'rxjs/operators';

// https://docs.microsoft.com/ru-ru/onedrive/developer/rest-api/api/driveitem_createuploadsession?view=odsp-graph-online
const CHUNK_SIZE = 320 * 1024; // 320 Kb

enum OperationStatus {
  Completed = 'completed',
  InProgress = 'inProgress',
}

@Injectable({ providedIn: 'root' })
export class OneDriveApiService {
  private graphUrl: string = 'https://graph.microsoft.com/v1.0';
  private myDrive: string = 'me/drive';

  constructor(
    private readonly http: HttpClient,
  ) { }

  async getRootMetadata(): Promise<OneDriveItemMetadata> {
    return await this.http.get(`${this.graphUrl}/${this.myDrive}/root`).toPromise() as OneDriveItemMetadata;
  }

  async getFileMetadata(fileId: string, driveId?: string): Promise<OneDriveItemMetadata> {
    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${fileId}`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${fileId}`;
    }

    return await this.http.get(url).toPromise() as OneDriveItemMetadata;
  }

  async getRootFiles(): Promise<Array<OneDriveItemMetadata>> {
    const response = await this.http.get(`${this.graphUrl}/${this.myDrive}/root/children`).toPromise() as OneDriveRespone;
    return response.value;
  }

  async getFiles(folderId: string, driveId?: string): Promise<Array<OneDriveItemMetadata>> {
    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${folderId}/children`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${folderId}/children`;
    }

    const response = await this.http.get(url).toPromise() as OneDriveRespone;
    return response.value;
  }

  async getSharedFiles(): Promise<Array<OneDriveItemMetadata>> {
    const response = await this.http.get(`${this.graphUrl}/${this.myDrive}/sharedWithMe`).toPromise() as OneDriveRespone;
    return response.value;
  }

  async createFolder(parentId: string, name: string, driveId?: string): Promise<any> {
    const body: CreateFolderRequestBody = {
      name,
      folder: {}
    };

    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${parentId}/children`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${parentId}/children`;
    }

    return await this.http.post(url, body).toPromise();
  }

  async copyFile(fileId: string, folder: OneDriveItemParent, name?: string, driveId?: string): Promise<void> {
    const body: CopyMoveRequestBody = {
      parentReference: folder,
      name,
    };

    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${fileId}/copy`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${fileId}/copy`;
    }

    const response = await this.http.post(url, body, { observe: 'response' }).toPromise() as HttpResponse<object>;
    const location = response.headers.get('Location');

    if (location) {
      await this.waitCopyingFinish(location);
    }
  }

  async moveFile(fileId: string, folder: OneDriveItemParent, name?: string, driveId?: string): Promise<OneDriveItemMetadata> {
    const body: CopyMoveRequestBody = {
      parentReference: folder,
      name,
    };

    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${fileId}`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${fileId}`;
    }

    const response = await this.http.patch(url, body).toPromise() as OneDriveItemMetadata;

    return Promise.resolve(response);
  }

  async deleteFile(fileId: string, driveId?: string): Promise<void> {
    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${fileId}`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${fileId}`;
    }

    await this.http.delete(url).toPromise();
  }

  async updateFileMetadata(fileId: string, metadata: Partial<OneDriveItemMetadata>, driveId?: string): Promise<OneDriveItemMetadata> {
    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${fileId}`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${fileId}`;
    }

    const response = await this.http.patch(url, metadata).toPromise() as OneDriveItemMetadata;
    return response;
  }

  async downloadFile(url: string): Promise<File> {
    const response = await this.http.get(url, { responseType: 'arraybuffer' }).toPromise() as ArrayBuffer;
    const blob = new File([response], 'test');

    return blob;
  }

  async uploadFile(parentId: string, fileName: string, file: File, isUpdate?: boolean, driveId?: string): Promise<string> {
    const uploadSession = await this.createUploadSession(parentId, fileName, isUpdate, driveId);

    let currentIndex = 0;

    while (currentIndex < file.size) {
      let blob: Blob;

      if (currentIndex + CHUNK_SIZE < file.size) {
        blob = file.slice(currentIndex, currentIndex + CHUNK_SIZE, file.type);
      } else {
        blob = file.slice(currentIndex, file.size, file.type);
      }

      const chunkOptions: ChunkOptions = {
        data: blob,
        length: blob.size,
        startIndex: currentIndex,
        endIndex: currentIndex + blob.size - 1,
        fullSize: file.size,
      };

      const response = await this.uploadChunk(uploadSession.uploadUrl, chunkOptions);

      if ((response as OneDriveItemMetadata).id) {
        return (response as OneDriveItemMetadata).id;
      }

      currentIndex += CHUNK_SIZE;
    }

    return '';
  }

  private async createUploadSession(parentId: string, name: string, isUpdate?: boolean,
    driveId?: string): Promise<CreateUploadSessionResponse> {
    const body = {
      item: {
        '@microsoft.graph.conflictBehavior': isUpdate ? 'replace' : 'rename',
        name
      }
    };

    let url: string;

    if (driveId) {
      url = `${this.graphUrl}/drives/${driveId}/items/${parentId}:/${name}:/createUploadSession`;
    } else {
      url = `${this.graphUrl}/${this.myDrive}/items/${parentId}:/${name}:/createUploadSession`;
    }

    const response = await this.http.post(url, body).toPromise() as CreateUploadSessionResponse;

    return Promise.resolve(response);
  }

  private async uploadChunk(url: string, chunkOptions: ChunkOptions): Promise<ChunkUploadResponse | OneDriveItemMetadata> {
    const options = {
      headers: {
        'Content-Range': `bytes ${chunkOptions.startIndex}-${chunkOptions.endIndex}/${chunkOptions.fullSize}`,
      }
    };

    const response = await this.http.put(url, chunkOptions.data, options).toPromise() as ChunkUploadResponse | OneDriveItemMetadata;
    return Promise.resolve(response);
  }

  private async waitCopyingFinish(url: string): Promise<void> {
    const checkingStatusRequest = this.http.get(url);

    const polling = () => timer(0, 500)
      .pipe(concatMap(() => from(checkingStatusRequest)))
      .pipe(filter((response: CopyingStatus) => response.status === OperationStatus.Completed))
      .pipe(take(1))
      .toPromise();

    await polling();
  }
}

export interface OneDriveItemMetadata {
  type: MetadataTypesEnum;
  id: string;
  name: string;
  parentReference: OneDriveItemParent;
  lastModifiedDateTime: string;
  file: {
    mimeType: string,
  };
  folder: {
    childCount: number
  };
  remoteItem: OneDriveItemMetadata;
  shared: {
    owner: { user: { id: string } }
  };
  size: number;
  fileSystemInfo: {
    lastModifiedDateTime: string;
    lastAccessedDateTime: string;
  }
  '@microsoft.graph.downloadUrl': string;
}

export interface OneDriveRespone {
  value: OneDriveItemMetadata[];
}

export interface OneDriveItemParent {
  driveId: string;
  id: string;
}

interface CopyMoveRequestBody {
  name?: string;
  parentReference: OneDriveItemParent;
}

interface CreateFolderRequestBody {
  name: string;
  folder: object;
}

interface CreateUploadSessionResponse {
  uploadUrl: string;
  expirationDateTime: string;
}

interface ChunkUploadResponse {
  expirationDateTime: string;
  nextExpectedRanges: Array<string>;
}

interface ChunkOptions {
  data: Blob;
  length: number;
  startIndex: number;
  endIndex: number;
  fullSize: number;
}

interface CopyingStatus {
  percentageComplete: string;
  status: OperationStatus;
}
